import {
  fetchTransferConfiguration,
  upsertTransferConfiguration,
} from '@/services/transfer.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { QueryOptions } from '@/types/common.type';
import type {
  TransferConfiguration,
  TransferConfigurationUpdateRequest,
} from '@/types/transfer.type';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

export const useTransferConfigurationQuery = (
  agentId: number,
  options?: Omit<UseQueryOptions<TransferConfiguration | null, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.transferConfigurations.detail(agentId),
    queryFn: () => fetchTransferConfiguration(agentId),
    ...options,
  });
};

export const useUpsertTransferConfigurationMutation = (
  agentId: number,
  options?: QueryOptions<TransferConfiguration>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.transferConfigurations.mutations.upsert,
    mutationFn: (payload: TransferConfigurationUpdateRequest) =>
      upsertTransferConfiguration(agentId, payload),
    onSuccess: data => {
      notifyMutationSuccessWithOptions(
        options,
        'Transfer settings saved',
        'Transfer configuration was saved successfully.'
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.transferConfigurations.detail(agentId),
      });
      options?.onSuccess?.(data);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Failed to save transfer settings', error);
      options?.onError?.(error);
    },
  });
};
